import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Building2, Loader2, MapPin, ChevronDown, ChevronUp, Settings2, ShieldCheck, AlertTriangle, Pencil, Save, X, Upload, FileText, CheckCircle } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";

const BUSINESS_TYPES = [
  { value: "private_sports_facility", label: "Private Sports Facility" },
  { value: "gym_studio", label: "Gym / Studio" },
  { value: "lgu_community", label: "LGU / Community" },
  { value: "mixed_use", label: "Mixed-Use / Multi-Purpose" },
];

const SPORT_OPTIONS = [
  "Basketball", "Badminton", "Volleyball", "Tennis", "Table Tennis",
  "Football", "Swimming", "Boxing", "Martial Arts", "Yoga",
  "Dance", "CrossFit", "Gym/Weights", "Running Track", "Other",
];

export default function ProviderVenues() {
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Edit Venue state
  const [editVenue, setEditVenue] = useState(null); // venue being edited
  const [editForm, setEditForm] = useState({});
  const [editDocs, setEditDocs] = useState([]);
  const [confirmEdit, setConfirmEdit] = useState(null); // venue pending confirmation
  const [editing, setEditing] = useState(false); // read-only -> editable toggle
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const fetchVenues = async () => {
    try { const res = await api.get("/provider/venues"); setVenues(res.data); } catch {}
    setLoading(false);
  };
  useEffect(() => { fetchVenues(); }, []);

  // Legacy ?create=1 redirects to new venue page
  useEffect(() => {
    if (searchParams.get("create") === "1") {
      const params = new URLSearchParams(searchParams);
      params.delete("create");
      setSearchParams(params, { replace: true });
      navigate("/provider/new-venue");
    }
  }, [searchParams, setSearchParams, navigate]);

  const openEditFlow = (v) => {
    const isApproved = v.ownership_verification_status === "verified";
    if (isApproved) {
      setConfirmEdit(v);
    } else {
      beginEdit(v);
    }
  };

  const beginEdit = (v) => {
    setConfirmEdit(null);
    setEditVenue(v);
    setEditForm({
      name: v.name || "",
      address: v.address || "",
      city: v.city || "",
      description: v.description || "",
      business_type: v.business_type || "",
      sport_types: v.sport_types || [],
      business_registration_number: v.business_registration_number || "",
      legal_entity_name: v.legal_entity_name || "",
      authorized_signatory_name: v.authorized_signatory_name || "",
      government_authorization: v.government_authorization || "",
      lgu_contact_person: v.lgu_contact_person || "",
      lgu_contact_number: v.lgu_contact_number || "",
      contact_phone: v.contact_phone || "",
      contact_email: v.contact_email || "",
      operating_hours: v.operating_hours || "",
    });
    setEditDocs(v.verification_documents || []);
    setEditing(v.ownership_verification_status !== "verified"); // approved -> read-only first
  };

  const closeEdit = () => {
    setEditVenue(null);
    setEditForm({});
    setEditDocs([]);
    setEditing(false);
  };

  const set = (key, val) => setEditForm(prev => ({ ...prev, [key]: val }));

  const toggleSport = (sport) => {
    setEditForm(prev => ({
      ...prev,
      sport_types: prev.sport_types?.includes(sport)
        ? prev.sport_types.filter(s => s !== sport)
        : [...(prev.sport_types || []), sport],
    }));
  };

  const handleDocUpload = async (e, docType) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/uploads/file", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = res.data.file_url || res.data.url;
      setEditDocs(prev => [...prev.filter(d => d.doc_type !== docType), { doc_type: docType, file_url: url, file_name: file.name, uploaded_at: new Date().toISOString() }]);
      toast.success(`${docType} uploaded`);
    } catch {
      toast.error("Upload failed");
    }
    setUploading(false);
  };

  const handleSaveEdit = async () => {
    if (!editForm.name || !editForm.city) { toast.error("Name and city are required"); return; }
    setSaving(true);
    try {
      const payload = { ...editForm, verification_documents: editDocs };
      const res = await api.put(`/venues/${editVenue.venue_id}`, payload);
      if (res.data?.resubmitted) {
        toast.success("Venue updated — re-submitted for verification");
      } else {
        toast.success("Venue updated");
      }
      closeEdit();
      fetchVenues();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update");
    }
    setSaving(false);
  };

  const isLGU = editForm.business_type === "lgu_community";
  const isPrivate = ["private_sports_facility", "gym_studio", "mixed_use"].includes(editForm.business_type);
  const requiredDocs = isLGU
    ? [
        { key: "gov_authorization", label: "Government / Community Authorization Letter" },
        { key: "facility_photos", label: "Facility Photos" },
        { key: "contact_proof", label: "LGU Contact Proof / ID" },
      ]
    : isPrivate ? [
        { key: "business_permit", label: "Business Permit / Registration" },
        { key: "facility_photos", label: "Facility Photos" },
        { key: "proof_of_ownership", label: "Proof of Ownership / Lease Agreement" },
        ...(editForm.business_type === "mixed_use" ? [{ key: "multi_use_authorization", label: "Multi-Use Authorization" }] : []),
      ] : [];

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-court-orange" /></div>;

  const isApproved = editVenue?.ownership_verification_status === "verified";
  const readOnly = isApproved && !editing;

  return (
    <div className="max-w-4xl mx-auto px-4 md:px-8 py-6 md:py-10" data-testid="provider-venues-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight">My Venues</h1>
        <Button data-testid="add-venue-btn" onClick={() => navigate("/provider/new-venue")}
          className="bg-court-orange hover:bg-court-orange-hover text-white rounded-full font-heading uppercase tracking-wide px-6 shadow-[3px_3px_0px_0px_rgba(0,0,0,1)]">
          <Plus className="w-4 h-4 mr-2" />Add Venue
        </Button>
      </div>

      {venues.length === 0 ? (
        <div className="text-center py-16 border-2 border-dashed border-slate-200">
          <Building2 className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <p className="font-heading text-lg text-muted-foreground uppercase">No venues yet</p>
          <p className="text-sm text-muted-foreground mt-1">Add your first venue to start accepting bookings</p>
        </div>
      ) : (
        <div className="space-y-4">
          {venues.map(v => (
            <div key={v.venue_id} className="bg-white border-2 border-slate-100" data-testid={`venue-item-${v.venue_id}`}>
              <div className="p-4 flex items-center gap-4 cursor-pointer" onClick={() => setExpanded(e => ({ ...e, [v.venue_id]: !e[v.venue_id] }))}>
                <div className="flex-1">
                  <h3 className="font-heading text-lg font-bold uppercase">{v.name}</h3>
                  <p className="text-sm text-muted-foreground flex items-center gap-1"><MapPin className="w-3 h-3" />{v.city} - {v.address}</p>
                  <div className="flex gap-1 mt-1 flex-wrap">
                    {v.sport_types?.map(s => <Badge key={s} variant="outline" className="text-[10px] font-heading uppercase">{s}</Badge>)}
                    {v.ownership_verification_status === "verified" ? (
                      <Badge className="bg-green-100 text-green-800 text-[10px]"><ShieldCheck className="w-3 h-3 mr-0.5" />Verified</Badge>
                    ) : v.ownership_verification_status === "under_review" ? (
                      <Badge className="bg-blue-100 text-blue-800 text-[10px]">Under Review</Badge>
                    ) : v.ownership_verification_status === "rejected" ? (
                      <Badge className="bg-red-100 text-red-800 text-[10px]">Rejected</Badge>
                    ) : (
                      <Badge className="bg-amber-100 text-amber-800 text-[10px]"><AlertTriangle className="w-3 h-3 mr-0.5" />Unverified</Badge>
                    )}
                  </div>
                </div>
                <div className="text-right flex items-center gap-2">
                  <div>
                    <p className="text-sm font-medium">{v.total_courts || (v.resources?.length || 0) + (v.physical_spaces?.length || 0)} courts / spaces</p>
                    <div className="flex gap-1 mt-1 justify-end">
                      <Button size="sm" variant="outline" className="text-[10px] h-6 px-2"
                        onClick={(e) => { e.stopPropagation(); openEditFlow(v); }}
                        data-testid={`edit-venue-${v.venue_id}`}>
                        <Pencil className="w-3 h-3 mr-1" />Edit
                      </Button>
                      <Button size="sm" variant="outline" className="text-[10px] h-6 px-2"
                        onClick={(e) => { e.stopPropagation(); navigate(`/provider/verify/${v.venue_id}`); }}
                        data-testid={`verify-${v.venue_id}`}>
                        <ShieldCheck className="w-3 h-3 mr-1" />{v.ownership_verification_status === "verified" ? "View" : "Verify"}
                      </Button>
                    </div>
                  </div>
                  {expanded[v.venue_id] ? <ChevronUp className="w-5 h-5 text-muted-foreground" /> : <ChevronDown className="w-5 h-5 text-muted-foreground" />}
                </div>
              </div>

              {expanded[v.venue_id] && (
                <div className="border-t border-slate-100 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-heading text-sm uppercase tracking-[0.15em] text-muted-foreground">Courts & Spaces</h4>
                    <Button data-testid={`create-space-${v.venue_id}`} size="sm" onClick={() => navigate(`/provider/spaces/${v.venue_id}`)}
                      className="bg-slate-900 hover:bg-slate-800 text-white rounded-full font-heading uppercase text-xs px-4">
                      <Plus className="w-3 h-3 mr-1" />Create Space / Court
                    </Button>
                  </div>
                  {(v.resources?.length || 0) + (v.physical_spaces?.length || 0) === 0 ? (
                    <p className="text-sm text-muted-foreground">No courts or spaces added yet</p>
                  ) : (
                    <div className="space-y-2">
                      {v.resources?.map(r => (
                        <div key={r.resource_id} className="flex items-center justify-between p-3 bg-slate-50 text-sm cursor-pointer hover:bg-slate-100 transition-colors"
                          data-testid={`court-item-${r.resource_id}`}
                          onClick={() => navigate(`/provider/courts/${r.resource_id}`)}>
                          <div>
                            <p className="font-medium">{r.name}</p>
                            <p className="text-xs text-muted-foreground capitalize">{r.sport_type} · {r.attributes?.surface || 'Standard'}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className={`text-[10px] ${r.status === 'ACTIVE' ? 'border-green-300 text-green-700' : 'border-red-300 text-red-600'}`}>{r.status || "Active"}</Badge>
                            <Button data-testid={`manage-court-${r.resource_id}`} size="sm" variant="outline"
                              onClick={(e) => { e.stopPropagation(); navigate(`/provider/courts/${r.resource_id}`); }}
                              className="rounded-full border-2 border-court-orange text-court-orange hover:bg-court-orange/5 font-heading uppercase text-[10px] px-3 h-7">
                              <Settings2 className="w-3 h-3 mr-1" />Manage
                            </Button>
                          </div>
                        </div>
                      ))}
                      {v.physical_spaces?.map(space => (
                        <div key={space.space_id || space.physical_space_id} className="flex items-center justify-between p-3 bg-blue-50 text-sm border border-blue-100 cursor-pointer hover:bg-blue-100 transition-colors"
                          data-testid={`space-item-${space.space_id || space.physical_space_id}`}
                          onClick={() => navigate(`/provider/spaces/${v.venue_id}`)}>
                          <div>
                            <p className="font-medium">{space.space_name || space.name}</p>
                            <p className="text-xs text-muted-foreground capitalize">{(space.space_type || "court").replace(/_/g, " ")} · {(space.surface_type || "Standard").replace(/_/g, " ")}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-[10px] border-blue-300 text-blue-700">
                              {space.classification === 'single_sport' ? 'Single Sport' : space.classification === 'facility' ? 'Facility' : space.classification === 'multi_sport' ? 'Multi-Sport' : 'Space'}
                            </Badge>
                            <Badge variant="outline" className={`text-[10px] ${space.lifecycle_status === 'published' || !space.lifecycle_status ? 'border-green-300 text-green-700' : 'border-amber-300 text-amber-600'}`}>
                              {space.lifecycle_status || "Active"}
                            </Badge>
                            <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); navigate(`/provider/spaces/${v.venue_id}`); }}
                              className="rounded-full border-2 border-blue-200 text-blue-700 text-[10px] px-3 h-7">
                              <Settings2 className="w-3 h-3 mr-1" />Manage
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Confirm Edit Approved Venue Warning */}
      <Dialog open={!!confirmEdit} onOpenChange={(o) => !o && setConfirmEdit(null)}>
        <DialogContent data-testid="confirm-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />Edit Approved Venue?
            </DialogTitle>
            <DialogDescription className="pt-2 text-sm">
              Changing your approved venue details will be subjected for another review.
              Your venue will be temporarily moved back to <strong>Pending Verification</strong> until our team re-approves the changes.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setConfirmEdit(null)} data-testid="cancel-edit-warning">Cancel</Button>
            <Button onClick={() => beginEdit(confirmEdit)} className="bg-court-orange hover:bg-court-orange-hover text-white" data-testid="proceed-edit-warning">
              I Understand, Proceed
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Venue Dialog */}
      <Dialog open={!!editVenue} onOpenChange={(o) => !o && closeEdit()}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="edit-venue-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase flex items-center gap-2">
              <Building2 className="w-5 h-5" />Edit Venue Details
              {isApproved && readOnly && <Badge className="bg-green-100 text-green-800 text-[10px] ml-2"><ShieldCheck className="w-3 h-3 mr-0.5" />Verified — Read Only</Badge>}
              {isApproved && editing && <Badge className="bg-amber-100 text-amber-800 text-[10px] ml-2"><AlertTriangle className="w-3 h-3 mr-0.5" />Editing — Will trigger re-review</Badge>}
            </DialogTitle>
          </DialogHeader>

          {isApproved && editing && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800 flex gap-2" data-testid="edit-warning-banner">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <p>Saving these changes will <strong>reset your venue's verification status to Pending</strong>. Our team will need to re-approve the updated details before your venue is publicly listed again.</p>
            </div>
          )}

          {isApproved && readOnly && (
            <div className="flex justify-end">
              <Button onClick={() => setEditing(true)} variant="outline" className="text-xs" data-testid="enable-edit-btn">
                <Pencil className="w-3 h-3 mr-1" />Enable Editing
              </Button>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <Label>Venue Name *</Label>
              <Input value={editForm.name} disabled={readOnly} onChange={e => set("name", e.target.value)} data-testid="edit-venue-name" />
            </div>
            <div>
              <Label>Address *</Label>
              <Input value={editForm.address} disabled={readOnly} onChange={e => set("address", e.target.value)} data-testid="edit-venue-address" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>City *</Label>
                <Input value={editForm.city} disabled={readOnly} onChange={e => set("city", e.target.value)} data-testid="edit-venue-city" />
              </div>
              <div>
                <Label>Operating Hours</Label>
                <Input value={editForm.operating_hours} disabled={readOnly} onChange={e => set("operating_hours", e.target.value)} data-testid="edit-venue-hours" />
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={editForm.description} disabled={readOnly} onChange={e => set("description", e.target.value)} rows={3} data-testid="edit-venue-description" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><Label>Contact Phone</Label><Input value={editForm.contact_phone} disabled={readOnly} onChange={e => set("contact_phone", e.target.value)} data-testid="edit-venue-phone" /></div>
              <div><Label>Contact Email</Label><Input value={editForm.contact_email} disabled={readOnly} onChange={e => set("contact_email", e.target.value)} data-testid="edit-venue-email" /></div>
            </div>

            <div>
              <Label className="mb-2 block">Sports & Activities Available</Label>
              <div className="flex flex-wrap gap-2">
                {SPORT_OPTIONS.map(sport => (
                  <button key={sport} type="button" disabled={readOnly} onClick={() => toggleSport(sport)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${editForm.sport_types?.includes(sport) ? "bg-court-green text-white border-court-green" : "bg-white text-slate-600 border-slate-200 hover:border-court-green/50"} ${readOnly ? "opacity-60 cursor-not-allowed" : ""}`}
                    data-testid={`edit-sport-${sport.toLowerCase().replace(/\s/g, "-")}`}>
                    {sport}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label>Business Type</Label>
              <Select value={editForm.business_type} disabled={readOnly} onValueChange={v => set("business_type", v)}>
                <SelectTrigger data-testid="edit-business-type"><SelectValue placeholder="Select business type" /></SelectTrigger>
                <SelectContent>
                  {BUSINESS_TYPES.map(bt => <SelectItem key={bt.value} value={bt.value}>{bt.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {isPrivate && (
              <div className="space-y-4 p-4 bg-slate-50 rounded-lg border" data-testid="edit-private-fields">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Private Business Details</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div><Label>Business Registration #</Label><Input value={editForm.business_registration_number} disabled={readOnly} onChange={e => set("business_registration_number", e.target.value)} /></div>
                  <div><Label>Legal Entity Name</Label><Input value={editForm.legal_entity_name} disabled={readOnly} onChange={e => set("legal_entity_name", e.target.value)} /></div>
                </div>
                <div><Label>Authorized Signatory</Label><Input value={editForm.authorized_signatory_name} disabled={readOnly} onChange={e => set("authorized_signatory_name", e.target.value)} /></div>
              </div>
            )}

            {isLGU && (
              <div className="space-y-4 p-4 bg-green-50 rounded-lg border border-green-200" data-testid="edit-lgu-fields">
                <p className="text-xs font-medium text-green-700 uppercase tracking-wider">Government / Community Details</p>
                <div><Label>Government / Community Authorization</Label><Textarea value={editForm.government_authorization} disabled={readOnly} onChange={e => set("government_authorization", e.target.value)} rows={2} /></div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div><Label>LGU Contact Person</Label><Input value={editForm.lgu_contact_person} disabled={readOnly} onChange={e => set("lgu_contact_person", e.target.value)} /></div>
                  <div><Label>LGU Contact Number</Label><Input value={editForm.lgu_contact_number} disabled={readOnly} onChange={e => set("lgu_contact_number", e.target.value)} /></div>
                </div>
              </div>
            )}

            {editForm.business_type && requiredDocs.length > 0 && (
              <div className="space-y-3">
                <Label className="flex items-center gap-2"><Upload className="w-4 h-4" />Verification Documents</Label>
                {requiredDocs.map(doc => {
                  const uploaded = editDocs.find(d => d.doc_type === doc.key);
                  return (
                    <div key={doc.key} className="flex items-center justify-between p-3 border rounded-lg" data-testid={`edit-doc-${doc.key}`}>
                      <div className="flex items-center gap-3 flex-1">
                        <FileText className="w-4 h-4 text-muted-foreground" />
                        <div>
                          <p className="text-sm font-medium">{doc.label}</p>
                          {uploaded && <p className="text-xs text-green-600 flex items-center gap-1"><CheckCircle className="w-3 h-3" />{uploaded.file_name}</p>}
                        </div>
                      </div>
                      {!readOnly && (
                        <label className="cursor-pointer">
                          <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={e => handleDocUpload(e, doc.key)} disabled={uploading} />
                          <span className={`px-3 py-1.5 text-xs font-medium rounded-full border ${uploaded ? "bg-green-50 text-green-700 border-green-200" : "bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100"}`}>
                            {uploading ? "Uploading..." : uploaded ? "Replace" : "Upload"}
                          </span>
                        </label>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <DialogFooter className="gap-2 pt-2">
            <Button variant="outline" onClick={closeEdit} data-testid="cancel-edit-btn"><X className="w-4 h-4 mr-1" />Close</Button>
            {!readOnly && (
              <Button onClick={handleSaveEdit} disabled={saving || uploading} className="bg-court-orange hover:bg-court-orange-hover text-white" data-testid="save-edit-venue-btn">
                {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : <><Save className="w-4 h-4 mr-2" />Save Changes</>}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
